import { useEffect, useState, useRef, useCallback } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import Editor from '@monaco-editor/react'
import { Plus, Pencil, Trash2, FileCode2, Search, X, ChevronLeft, ChevronRight, EllipsisVertical } from 'lucide-react'
import { snippetApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button, buttonVariants } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Badge, Spinner, EmptyState, Label } from '@/components/ui/misc'
import { Input } from '@/components/ui/Input'
import { LiquidEditor } from '@/components/designer/LiquidEditor'
import { cn } from '@/lib/utils'
import { toastApiError } from '@/lib/apiError'
import type { Snippet, SnippetType } from '@/types'
import { SNIPPET_TYPE_LABELS } from '@/types'

type FormState = {
  name: string
  description: string
  content: string
  type: SnippetType
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  content: '',
  type: 'Template',
}

const TYPE_TABS: Array<{ value: SnippetType | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'Template', label: 'Template' },
  { value: 'DataSource', label: 'Data Source' },
  { value: 'InputParameter', label: 'Input Parameter' },
]

const PAGE_SIZE = 10

function snippetTypeBadgeVariant(type: SnippetType): 'accent' | 'green' | 'default' {
  if (type === 'Template') return 'accent'
  if (type === 'DataSource') return 'green'
  return 'default'
}

export default function SnippetsPage() {
  const { toast } = useToast()

  const [snippets, setSnippets] = useState<Snippet[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<SnippetType | 'all'>('all')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Snippet | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Snippet | null>(null)
  const [deleting, setDeleting] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(searchTimer.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await snippetApi.getAll({
        filter: debouncedSearch || undefined,
        type: typeFilter === 'all' ? undefined : typeFilter,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      })
      setSnippets(res.items)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load snippets')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, typeFilter, page, toast])

  useEffect(() => { load() }, [load])

  function openCreate() {
    setForm({ ...EMPTY_FORM, type: typeFilter === 'all' ? 'Template' : typeFilter })
    setEditing(null)
    setCreating(true)
  }

  function openEdit(snippet: Snippet) {
    setForm({
      name: snippet.name,
      description: snippet.description,
      content: snippet.content,
      type: snippet.type,
    })
    setEditing(snippet)
    setCreating(true)
  }

  function closeForm() {
    setCreating(false)
    setEditing(null)
  }

  async function handleSave() {
    if (!form.name.trim() || !form.content.trim()) {
      return toast('Name and content are required', 'error')
    }
    setSaving(true)
    try {
      if (editing) {
        await snippetApi.update(editing.id, {
          ...form,
          version: editing.version ?? 1,
        })
        toast(`"${form.name}" updated`)
      } else {
        await snippetApi.create(form)
        toast(`"${form.name}" created`)
      }
      closeForm()
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await snippetApi.delete(deleteTarget.id)
      toast(`"${deleteTarget.name}" deleted`)
      setDeleteTarget(null)
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  function clearSearch() {
    setSearch('')
    searchRef.current?.focus()
  }

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, totalCount)
  const isSqlType = form.type === 'DataSource' || form.type === 'InputParameter'

  return (
    <div className="flex h-full overflow-hidden">
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-4 border-b border-border px-6 py-4 bg-surface flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-sm font-black tracking-wider text-ink">SNIPPETS</h1>
            <p className="text-xs text-muted font-mono mt-0.5">
              {loading ? 'Loading…' : `${totalCount} snippet${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          <div className="flex-1 max-w-sm relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
            <input
              ref={searchRef}
              className="w-full rounded-md border border-border2 bg-bg pl-8 pr-8 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
              placeholder="Search by name or description…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex-1" />
          <Button onClick={openCreate}><Plus className="h-3.5 w-3.5" /> New Snippet</Button>
        </div>

        <div className="flex gap-1 border-b border-border bg-surface px-6 py-2 flex-shrink-0">
          {TYPE_TABS.map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => { setTypeFilter(tab.value); setPage(1) }}
              className={cn(
                'rounded-md px-3 py-1.5 text-[10px] font-bold tracking-wide transition-colors',
                typeFilter === tab.value
                  ? 'bg-accent/10 text-accent'
                  : 'text-muted hover:text-ink hover:bg-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>
          ) : snippets.length === 0 ? (
            <EmptyState message={debouncedSearch ? `No snippets match "${debouncedSearch}"` : 'No snippets yet.'} />
          ) : (
            <div className="grid gap-3">
              {snippets.map(s => (
                <div
                  key={s.id}
                  className="flex items-center gap-4 rounded-lg border border-border bg-surface px-5 py-4 hover:border-border2 transition-colors cursor-pointer"
                  onClick={() => openEdit(s)}
                >
                  <FileCode2 className="h-4 w-4 text-accent flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-ink font-medium">{s.name}</span>
                      <Badge variant={snippetTypeBadgeVariant(s.type)}>
                        {SNIPPET_TYPE_LABELS[s.type]}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted mt-0.5 truncate">{s.description || 'No description'}</p>
                  </div>
                  {s.updatedAt && (
                    <p className="text-[10px] text-muted font-mono hidden sm:block flex-shrink-0">
                      {new Date(s.updatedAt).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex shrink-0 items-center" onClick={e => e.stopPropagation()}>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'text-muted hover:text-ink')}
                          aria-label={`Actions for ${s.name}`}
                        >
                          <EllipsisVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content align="end" sideOffset={6} className="z-50 min-w-[12.5rem] rounded-lg border border-border bg-surface py-1 shadow-xl">
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                            onSelect={() => openEdit(s)}
                          >
                            <Pencil className="h-3.5 w-3.5 shrink-0 text-muted" />
                            Edit
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-red outline-none data-[highlighted]:bg-red/10"
                            onSelect={() => setDeleteTarget(s)}
                          >
                            <Trash2 className="h-3.5 w-3.5 shrink-0" />
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 flex-shrink-0">
            <p className="text-[11px] text-muted font-mono">
              Showing <span className="text-ink">{from}–{to}</span> of <span className="text-ink">{totalCount}</span>
            </p>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce<(number | '...')[]>((acc, p, i, arr) => {
                  if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} className="w-8 text-center text-xs text-muted">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={cn(
                        'h-8 min-w-8 px-2 rounded-md text-xs font-mono transition-colors',
                        page === p ? 'bg-accent text-white font-bold' : 'text-muted hover:text-ink hover:bg-border'
                      )}
                    >{p}</button>
                  )
                )}
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {creating && (
        <div className="w-[28rem] border-l border-border bg-surface flex flex-col flex-shrink-0">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-xs font-black tracking-wider text-ink">
              {editing ? 'EDIT SNIPPET' : 'NEW SNIPPET'}
            </h2>
            <Button variant="ghost" size="sm" onClick={closeForm}>Cancel</Button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="TemplateStyleBlock"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              />
              <p className="mt-1 text-[10px] text-muted font-mono">Must be unique across all snippets</p>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Table styling for resident reports"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="w-full rounded-md border border-border2 bg-bg px-3 py-2 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                value={form.type}
                onChange={e => setForm(p => ({ ...p, type: e.target.value as SnippetType }))}
              >
                {(['Template', 'DataSource', 'InputParameter'] as SnippetType[]).map(t => (
                  <option key={t} value={t}>{SNIPPET_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Content</Label>
              {form.type === 'Template' ? (
                <div className="h-64 rounded-md border border-border2 overflow-hidden">
                  <LiquidEditor
                    value={form.content}
                    onChange={v => setForm(p => ({ ...p, content: v }))}
                    height="256px"
                  />
                </div>
              ) : (
                <div className="h-64 rounded-md border border-border2 overflow-hidden">
                  <Editor
                    height="256px"
                    language="sql"
                    theme="vs-dark"
                    value={form.content}
                    onChange={v => setForm(p => ({ ...p, content: v ?? '' }))}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 12,
                      lineNumbers: 'on',
                      wordWrap: 'on',
                      scrollBeyondLastLine: false,
                    }}
                  />
                </div>
              )}
              {isSqlType && (
                <p className="mt-1 text-[10px] text-muted font-mono">Only SELECT statements are allowed</p>
              )}
            </div>
          </div>
          <div className="border-t border-border p-5">
            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update Snippet' : 'Create Snippet'}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="DELETE SNIPPET"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This action cannot be undone.` : ''}
        loading={deleting}
        onCancel={() => { if (!deleting) setDeleteTarget(null) }}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
